import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Pagination,
  Alert,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import Layout from '../../components/Layout';

interface Campana {
  id: number;
  nombre: string;
  descripcion: string;
  fecha_inicio: string;
  fecha_fin: string;
  aplica_todo_negocio: boolean;
  locales_aplicables: string[];
  consumo_minimo: number;
  descuento_maximo: number;
  porcentaje_descuento: number;
  monto_descuento: number;
  numero_promociones: number;
  promociones_usadas: number;
  activo: boolean;
  created_at: string;
  updated_at: string;
  total_redenciones: number;
  monto_total_redenciones: number;
}

interface PaginationInfo {
  current_page: number;
  total_pages: number;
  total_items: number;
  items_per_page: number;
}

const Campanas: React.FC = () => {
  const [campanas, setCampanas] = useState<Campana[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo>({
    current_page: 1,
    total_pages: 0,
    total_items: 0,
    items_per_page: 10,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Filtros
  const [filtroActivo, setFiltroActivo] = useState<string>('');

  const loadCampanas = async (page: number = 1, activo?: string) => {
    setLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '10'
      });
      
      if (activo) {
        params.append('activo', activo);
      }

      const response = await fetch(`/api/comercio/campanas?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (data.success) {
        setCampanas(data.data.campanas);
        setPagination(data.data.pagination);
      } else {
        setError(data.message || 'Error cargando campañas');
      }
    } catch (error) {
      console.error('Error loading campanas:', error);
      setError('Error de conexión al cargar campañas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCampanas();
  }, []);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 8000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    loadCampanas(page, filtroActivo);
  };

  const handleFiltroActivoChange = (event: any) => {
    const newFiltro = event.target.value;
    setFiltroActivo(newFiltro);
    loadCampanas(1, newFiltro || undefined);
  };

  const formatFecha = (fecha: string) => {
    if (!fecha) return '-';
    return new Date(fecha).toLocaleDateString('es-PE');
  };

  const formatMonto = (monto: number | null | undefined) => {
    if (monto === null || monto === undefined || isNaN(Number(monto))) {
      return 'S/ 0.00';
    }
    return `S/ ${Number(monto).toFixed(2)}`;
  };

  const getEstadoCampana = (campana: Campana) => {
    const hoy = new Date();
    const fechaInicio = new Date(campana.fecha_inicio);
    const fechaFin = new Date(campana.fecha_fin);
    
    if (!campana.activo) {
      return { estado: 'Inactiva', color: 'error' as const };
    }
    
    if (hoy < fechaInicio) {
      return { estado: 'Programada', color: 'info' as const };
    }
    
    if (hoy > fechaFin) {
      return { estado: 'Finalizada', color: 'warning' as const };
    }
    
    return { estado: 'Activa', color: 'success' as const };
  };

  const getTipoDescuento = (campana: Campana) => {
    if (campana.porcentaje_descuento > 0) {
      return `${campana.porcentaje_descuento}% OFF`;
    }
    if (campana.monto_descuento > 0) {
      return `${formatMonto(campana.monto_descuento)} OFF`;
    }
    return 'Sin descuento';
  };

  return (
    <Layout>
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Mis Campañas
        </Typography>

        {/* Mensaje informativo */}
        <Alert 
          severity="info" 
          icon={<InfoIcon />}
          sx={{ mb: 3 }}
        >
          <Typography variant="body2">
            <strong>Información importante:</strong> La creación, edición y eliminación de campañas 
            es realizada por el equipo de Operaciones de Mascotas365. Esta sección le permite 
            visualizar sus campañas activas e historial de uso.
          </Typography>
        </Alert>

        {/* Mensaje de error */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Filtros */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Estado</InputLabel>
                <Select
                  value={filtroActivo}
                  label="Estado"
                  onChange={handleFiltroActivoChange}
                >
                  <MenuItem value="">Todos</MenuItem>
                  <MenuItem value="true">Activas</MenuItem>
                  <MenuItem value="false">Inactivas</MenuItem>
                </Select>
              </FormControl>

              <Typography variant="body2" color="text.secondary">
                Total: {pagination.total_items} campañas
              </Typography>
            </Box>
          </CardContent>
        </Card>

        {/* Tabla de campañas */}
        <Card>
          <CardContent>
            {loading ? (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Campaña</TableCell>
                        <TableCell>Fechas</TableCell>
                        <TableCell>Descuento</TableCell>
                        <TableCell align="center">Estado</TableCell>
                        <TableCell align="center">Usos</TableCell>
                        <TableCell align="center">Monto Total</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {campanas.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                            <Typography variant="body2" color="text.secondary">
                              No se encontraron campañas
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        campanas.map((campana) => {
                          const estadoCampana = getEstadoCampana(campana);
                          return (
                            <TableRow key={campana.id} hover>
                              <TableCell>
                                <Box>
                                  <Typography variant="body2" fontWeight="medium">
                                    {campana.nombre}
                                  </Typography>
                                  {campana.descripcion && (
                                    <Typography variant="caption" color="text.secondary">
                                      {campana.descripcion}
                                    </Typography>
                                  )}
                                  {campana.consumo_minimo > 0 && (
                                    <Typography variant="caption" display="block" color="text.secondary">
                                      Consumo mínimo: {formatMonto(campana.consumo_minimo)}
                                    </Typography>
                                  )}
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Box>
                                  <Typography variant="body2">
                                    {formatFecha(campana.fecha_inicio)}
                                  </Typography>
                                  <Typography variant="body2">
                                    {formatFecha(campana.fecha_fin)}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Box>
                                  <Typography variant="body2" fontWeight="medium">
                                    {getTipoDescuento(campana)}
                                  </Typography>
                                  {campana.descuento_maximo > 0 && (
                                    <Typography variant="caption" color="text.secondary">
                                      Máx: {formatMonto(campana.descuento_maximo)}
                                    </Typography>
                                  )}
                                </Box>
                              </TableCell>
                              <TableCell align="center">
                                <Chip
                                  label={estadoCampana.estado}
                                  color={estadoCampana.color}
                                  size="small"
                                  icon={campana.activo ? <ActiveIcon /> : <InactiveIcon />}
                                />
                              </TableCell>
                              <TableCell align="center">
                                <Box>
                                  <Typography variant="body2" fontWeight="medium">
                                    {campana.total_redenciones}
                                  </Typography>
                                  {campana.numero_promociones > 0 && (
                                    <Typography variant="caption" color="text.secondary">
                                      de {campana.numero_promociones}
                                    </Typography>
                                  )}
                                </Box>
                              </TableCell>
                              <TableCell align="center">
                                <Typography variant="body2" fontWeight="medium" color="primary">
                                  {formatMonto(campana.monto_total_redenciones)}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Paginación */}
                {pagination.total_pages > 1 && (
                  <Box display="flex" justifyContent="center" mt={3}>
                    <Pagination
                      count={pagination.total_pages}
                      page={pagination.current_page}
                      onChange={handlePageChange}
                      color="primary"
                      showFirstButton
                      showLastButton
                    />
                  </Box>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </Box>
    </Layout>
  );
};

export default Campanas;
